// All or portions of this file Copyright © 2019 Amazon Web Services, Inc. or its affiliates or licensors.
// All rights reserved. For full license terms and conditions, please see the LICENSE file at the root of
// this distribution (the "License") or go to https://content.sumerian.amazonaws.com/engine/latest/license.txt.
// All use of the software is governed by the License. All materials are made available on an "AS IS" BASIS, WITHOUT
// WARRANTIES OR CONDITIONS OF ANY KIND, express or implied. Do not remove or modify any license notices.
import System from 'core/entities/systems/System';
import SystemBus from 'core/entities/SystemBus';
import ObjectUtils from 'core/util/ObjectUtils';

/* global AWS */

const ELEMENT_ID = '__AWS_SDK_SCRIPT__';

/**
 * Configures the AWS SDK with AWS.CognitoIdentityCredentials.
 */
class AwsSystem extends System {

  constructor() {

    let interests = []; // array of component types we're interested in
    super('AwsSystem', interests);

    this._isReady = false;
    this._isStarted = false;

    // This is the AwsSystem config from the perspective of the
    // Sumerian EntityComponentSystem
    this._config = {};

    // This is the AWS SDK Config

    this._sdkConfig = {
      customUserAgent : 'SumerianScene'
    };

    this._region = null;

    // This system is always passive.
    this.passive = true;

    // Run last so that scripts and state machines will have been initialized
    // when the AwsSystem.SDK_READY system bus message is sent.
    this.priority = System.PRIORITY_LAST;

    this._isInitialLoad = true;
  }

  get region() {
    return this._sdkConfig.region;
  }

  get cognitoIdentityPoolId() {
    return this._config.cognitoIdentityPoolId;
  }


  // Any config here is merged into the global SDK config
  // Call this before the scene is loaded to override credentials/etc
  set sdkConfig(config) {
    this._sdkConfig = Object.assign(this._sdkConfig, config);
  }

  get sdkConfig() {
    return this._sdkConfig;
  }

  get sdk() {
    if(document.getElementById(ELEMENT_ID)) {
      return document.getElementById(ELEMENT_ID).src;
    } else {
      return undefined;
    }
  }

  /**
   * Configures the AWS SDK using the scene configuration data and runner
   * options.
   *
   * @param {Object} config - configuration object
   * @param {string} config.cognitoIdentityPoolId - id of identity pool to use
   * @param {string} config.initIotDataClient - initialize the IoT Data client or not
   * @param {string} config.awsSdkUrl - url of AWS SDK to download
   * @param {Object} options - configuration options (no currently used)
   *
   * @returns {Promise} that is resolved when the configuration has been
   * applied.
   */
  updateConfig(config, options = {}) {

    // config is undefined when there is no aws property in the scene's config.
    config = config || {};

    // We may get multiple updates in succession, no need to duplicate clone.
    //
    // But we don't handle cases where the config changes while the async process
    // below is already in progress. We also don't support changing the sdk
    // url once it is already set. https://issues.amazon.com/issues/SUMERIAN-1891

    if(!this._config || !ObjectUtils.isShallowEqual(this._config, config)) {
      this._config = ObjectUtils.clone(config);
    }

    if (!config.cognitoIdentityPoolId && !this.sdkConfig.credentials) {
      // If none of our credential options are configured, just bail out
      this._clearCredentials();
      return Promise.resolve();
    }

    // Load the SDK if it's not already loaded
    let promise = this._ensureSDK(config.sdkUrl);

    // skip cognito setup if the credential object has already been configured
    if (config.cognitoIdentityPoolId && !this.sdkConfig.credentials) {
      promise = promise.then(() => {
        this._setupCognitoIdentityCredentials(config.cognitoIdentityPoolId);
      });
    }

    promise = promise.then(() => AWS.config.update(this.sdkConfig));

    if (config.initIotDataClient) {
      promise = promise.then(() => {
        return this._initIotDataClient();
      });
    }

    promise = promise.then(() => {
      this._readyToStart();
    });

    return promise.catch((e) => {
      // Add a general error handler
      // to the AWSSystem
      // Unlike other systems, AWSSystem relies
      // on side effects that occur over the network
      // We don't want to kill the entire loading of the
      // scene if a user mistyped the SDK URL or cognito pool
      console.error(e);
      SystemBus.emit('sumerian.warning', {
        title: 'AWS SDK Initialization Failed',
        message: `AWS SDK Initialization Failed: ${e}`,
      });
    });
  }

  start() {
    this._isStarted = true;
    if(this._isReady) {
      this._ready();
    }
  }

  stop() {
    this._isStarted = false;
    this._isReady = false;
  }

  // Given a cognito Id, infer the AWS region from the poolId
  // and configure the AWS SDK to use this pool unauthenticated
  _setupCognitoIdentityCredentials(cognitoIdentityPoolId) {
    this._sdkConfig = Object.assign(this.sdkConfig, {
      region: this._getRegionFromCognitoIdentityPoolId(cognitoIdentityPoolId),
      credentials : new AWS.CognitoIdentityCredentials(
      {
        IdentityPoolId: cognitoIdentityPoolId
      })});
  }

  _getRegionFromCognitoIdentityPoolId(id) {
    const match = /(.*?):.+/.exec(id); // want up to first :
    if (match) {
      return match[1];
    } else {
      return undefined;
    }
  }

  // Dynamically load the AWS SDK configured in the scene bundle
  // If an AWS sdk is already loaded on the window, then this function is a no-op
  _ensureSDK(sdkUrl) {

    let promise;
    if (window.AWS) {
      promise = Promise.resolve();
    } else {

      promise = new Promise((resolve, reject) => {
        let script = document.getElementById(ELEMENT_ID);
        if (!script) {
          const head = document.getElementsByTagName('head')[0];
          script = document.createElement('script');
          script.id = ELEMENT_ID;
          script.type = 'text/javascript';
          script.src = sdkUrl;
          head.appendChild(script);
        }

        script.addEventListener('load', () => resolve());
        script.addEventListener('error', e => {
          // Remove the <script> element
          // so the next updateConfig will try again
          script.parentNode.removeChild(script);
          reject(new Error(`Failed to load ${sdkUrl}`));
        });
      });

    }

    return promise;
  }

  _initIotDataClient() {
    return new Promise((resolve, reject) => {

      // We have to start sending the endpointType parameter due to the following:
      // https://aws.amazon.com/blogs/iot/aws-iot-core-ats-endpoints/
      // Unfortunately, many Sumerian customers are using 2.138 but this parameter was not added until 2.159.0
      // So for this call we turn off parameter validation. This parameter is ignored in old SDK versions
      // but used in SDK versions that support this parameter.
      // This means after this change goes out, our customers affected by this issue just need
      // to update their SDK version


      const sdkConfig = Object.assign({}, this.sdkConfig, {paramValidation: false});
      const iot = new AWS.Iot(sdkConfig);

      iot.describeEndpoint({endpointType: "iot:Data-ATS"}, (error, data) => {
        if (error) {
          return resolve();
        }

        window.AWSIotData = new AWS.IotData(Object.assign({}, {endpoint: data.endpointAddress}, this.sdkConfig));

        resolve();
      });
    });
  }

  _clearCredentials() {
    if (this.sdkConfig.credentials && AWS) {
      // If SDK is still using credentials we created...
      if (this.sdkConfig.credentials === AWS.config.credentials) {
        AWS.config.credentials = null;
      }

      this.sdkConfig.credentials = null;
    }

    return Promise.resolve();
  }

  /**
   * Emit event indicating we're ready to start (cognito Id processed), but not necessarily started yet.
   * @private
   */
  _readyToStart() {
    this._isReady = true;

    if (this._isInitialLoad) {
      console.info('[Sumerian] Loaded AWS SDK version ' + AWS.VERSION);
      this._isInitialLoad = false;
    }

    SystemBus.emit(
      AwsSystem.SDK_READY_TO_START,
      {},   // data
      true  // store (so new listeners are told immediately)
    );
    if (this._isStarted) {
      this._ready();
    }
  }

  /**
   * Emit event indicating we're ready to start (cognito Id processed) and started.
   * @private
   */
  _ready() {
    SystemBus.emit(
      AwsSystem.SDK_READY,
      {region: this.sdkConfig.region}, // data
      true  // store (so new listeners are told immediately)
    );
  }
}

AwsSystem.SDK_READY = 'aws.sdkReady';
AwsSystem.SDK_READY_TO_START = 'aws.sdkReadyToStart';

export default AwsSystem;
